import React, { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import ReactSelect from "react-select";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/20/solid";
import { getEvaluatorQuestionsByUUID, getStatuses } from "../../services/questionService";
import { filterSelectStyles, selectTheme } from "../../components/forms/Select/styles";
import FilterChips, { CHIP_TONES } from "../../components/QuestionList/FilterChips";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { QuestionStatusBadge, STATUS_ORDER, statusText } from "../../components/QuestionList/questionStatus";
import { codeUnderName } from "../../components/QuestionList/cells";
import { focusRing, useMediaQuery } from "../Exams/TestBuilder/ui";
import { isOpenClick } from "../../utils/rowLink";
import { isCanceledError } from "../../utils/examError";
import { formatDateTime } from "../../utils/formatDate";

const PAGE_SIZE = 20;
// The search applies this long after the typing stops.
const SEARCH_DELAY = 350;

// The expert's vote as the API gives it (evaluation_value) and its value for the vote filter.
const VOTES = {
  Upvoted: { filter: "1", label: "evaluators.upvoted", Icon: HandThumbUpIcon, tone: CHIP_TONES.green, quote: "border-emerald-300" },
  Downvoted: { filter: "-1", label: "evaluators.downvoted", Icon: HandThumbDownIcon, tone: CHIP_TONES.red, quote: "border-red-300" },
};

// The avatar as in the experts list (Table.js): the same calm grey for everyone (colour here means a vote or a
// status) and the first letters of the first two words of the name. Keep the two in step.
const AVATAR_TONE = "bg-slate-100 text-slate-700 ring-slate-600/15";
const initials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

// evaluation_date is Python's text of a datetime ("2025-12-02 09:42:39.151196+00:00"). With a "T" and
// milliseconds it is an ISO date every browser reads (Safari has no date for the form with a space).
const isoDate = (value) =>
  typeof value === "string" ? value.trim().replace(" ", "T").replace(/(\.\d{3})\d+/, "$1") : value;

const statusRank = (code) => {
  const index = STATUS_ORDER.indexOf(code);
  return index === -1 ? STATUS_ORDER.length : index;
};

const messageCard = "flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm";
const secondaryButtonClass = `mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;

/**
 * One evaluation: the question (its name is the row's one link, with a new-tab icon; it opens in a new tab, as a
 * click on the row does, so the dialog keeps its filters and page), its status, code and subtopic, the expert's
 * vote with its date, and their comment. `wide` lays the evaluations out as rows of one card (the vote and the
 * comment in a second column from lg up); phones get one card each.
 */
function EvaluationItem({ evaluation, wide, language }) {
  const [t] = useTranslation();
  const href = `/questions/${evaluation.uuid}`;
  const vote = Object.prototype.hasOwnProperty.call(VOTES, evaluation.evaluation_value) ? VOTES[evaluation.evaluation_value] : null;
  const VoteIcon = vote?.Icon;
  const when = isoDate(evaluation.evaluation_date);
  const date = formatDateTime(when, language);
  const dateLabel = t("evaluators.evaluated_on", { date });
  const subtopic = typeof evaluation.subtopic === "string" ? evaluation.subtopic : evaluation.subtopic?.name;
  const code = codeUnderName(evaluation);
  const meta = [code, subtopic].filter(Boolean);
  // Older votes saved the text "no comment" for an empty comment (the server no longer does): shown as none.
  const rawComment = typeof evaluation.evaluation_comment === "string" ? evaluation.evaluation_comment.trim() : "";
  const comment = rawComment.toLowerCase() === "no comment" ? "" : rawComment;
  const newTab = t("evaluators.my_evaluations.new_tab");

  const open = (event) => {
    if (isOpenClick(event)) window.open(href, "_blank", "noopener");
  };

  return (
    <li
      onClick={open}
      onAuxClick={(event) => event.button === 1 && open(event)}
      className={
        wide
          ? "cursor-pointer px-5 py-4 transition-colors duration-150 hover:bg-slate-50/80"
          : "cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
      }
    >
      <div className="grid grid-cols-1 gap-y-2.5 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-x-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              title={t("evaluators.view_question")}
              className={`group relative min-w-0 break-words rounded text-[15px] font-semibold leading-snug text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
            >
              {evaluation.name || evaluation.code || "—"}
              <ArrowTopRightOnSquareIcon
                className="ml-1 inline-block h-3.5 w-3.5 align-[-0.125em] text-slate-400 transition-colors duration-200 group-hover:text-blue-600"
                aria-hidden="true"
              />
              <span className="sr-only"> ({newTab})</span>
            </a>
            <QuestionStatusBadge status={evaluation.status} />
          </div>
          {meta.length > 0 && (
            <p className="mt-0.5 break-words text-sm text-slate-500">
              {meta.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " · "}
                  <span className={index === 0 && code ? "font-mono text-xs" : undefined}>{part}</span>
                </React.Fragment>
              ))}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {vote && (
              <span
                className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${vote.tone.badge}`}
              >
                <VoteIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(vote.label)}
              </span>
            )}
            {date && (
              <span className="relative inline-flex items-center gap-1 text-sm text-slate-500" title={dateLabel}>
                <ClockIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="sr-only">{dateLabel}</span>
                <time dateTime={when} aria-hidden="true" className="tabular-nums">
                  {date}
                </time>
              </span>
            )}
          </div>
          {comment ? (
            <blockquote className={`relative mt-2 max-w-3xl border-l-2 pl-3 ${vote ? vote.quote : "border-slate-200"}`}>
              <span className="sr-only">{t("evaluators.modal.evaluator_comment")} </span>
              <p className="whitespace-pre-line break-words text-sm leading-relaxed text-slate-700">{comment}</p>
            </blockquote>
          ) : (
            <p className="mt-2 text-sm italic text-slate-500">{t("evaluators.no_comment")}</p>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * The dialog's body: the vote chips, the search and the status filter, then one page of the expert's
 * evaluations. It mounts with the dialog, so every opening starts from all the evaluations and page 1.
 * `statuses` fill the status filter (null while they load); Escape on the status filter with its menu closed
 * calls `onClose` (react-select keeps that key from the dialog).
 */
function EvaluatorEvaluations({ evaluator, statuses, onClose }) {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const uuid = evaluator.uuid;
  // Wide screens: the evaluations as rows of one card and the long search placeholder; phones: one card each.
  const wide = useMediaQuery("(min-width: 640px)");
  // The results line: a page change brings the focus back to it (goToPage).
  const resultsRef = useRef(null);

  // The search box's text; `search` is the one applied, SEARCH_DELAY later.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // "all", or the vote filter's value ("1" up, "-1" down); the status: "all" or a status uuid.
  const [vote, setVote] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);

  const [evaluations, setEvaluations] = useState([]);
  // null until the first answer, so the count does not start at "0".
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // "Try again" bumps it to send the same request again.
  const [attempt, setAttempt] = useState(0);

  // A new search starts from page 1.
  useEffect(() => {
    const value = searchInput.trim();
    if (value === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  // One page of the expert's evaluations; a newer request aborts the older one. While it loads, the page
  // already shown stays (dimmed), so the dialog keeps its size.
  useEffect(() => {
    if (!uuid) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    getEvaluatorQuestionsByUUID({
      uuid,
      params: {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        evaluation_value: vote !== "all" ? vote : undefined,
        status: status !== "all" ? status : undefined,
      },
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        const results = Array.isArray(data) ? data : data?.results || [];
        // An empty page past the first one (as a 404 below): back to the first one.
        if (!results.length && page > 1) {
          setPage(1);
          return;
        }
        setEvaluations(results);
        setCount(typeof data?.count === "number" ? data.count : results.length);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        // A page that no longer exists (fewer evaluations than before): back to the first one.
        if (err?.response?.status === 404 && page > 1) {
          setPage(1);
          return;
        }
        console.error("Failed to fetch the evaluator's evaluations:", err);
        setEvaluations([]);
        setCount(null);
        setFailed(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [uuid, page, search, vote, status, attempt]);

  // Every filter change starts from page 1.
  const changeVote = (value) => {
    setVote(value);
    setPage(1);
  };
  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };
  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setVote("all");
    setStatus("all");
    setPage(1);
  };

  // While the page loads the pagers are disabled, so they lose the focus: it goes to the results line. The
  // bottom pager also brings that line (the top of the list) into view.
  const goToPage = (next, { scroll = false } = {}) => {
    setPage(next);
    resultsRef.current?.focus({ preventScroll: true });
    if (scroll) resultsRef.current?.scrollIntoView({ block: "start" });
  };

  const pageCount = Math.ceil((count || 0) / PAGE_SIZE);
  const filtersActive = searchInput.trim() !== "" || vote !== "all" || status !== "all";
  // The list shown answers the filters applied (the search applies after its pause): its empty state follows them.
  const listFiltered = search !== "" || vote !== "all" || status !== "all";

  const voteItems = [VOTES.Upvoted, VOTES.Downvoted].map((v) => ({ value: v.filter, label: t(v.label), tone: v.tone }));

  // Statuses in the order a question goes through them, named as on the badges.
  const statusSelectOptions = [
    { value: "all", label: t("exam_taking.all_statuses") },
    ...[...(statuses || [])]
      .sort((a, b) => statusRank(a.code) - statusRank(b.code))
      .map((s) => ({ value: s.uuid, label: statusText(t, s.code, s.name) })),
  ];

  const filters = [
    {
      key: "status",
      active: status !== "all",
      node: (
        <ReactSelect
          className="relative"
          aria-label={t("question_list.status_filter")}
          styles={filterSelectStyles}
          theme={selectTheme}
          options={statusSelectOptions}
          value={statusSelectOptions.find((o) => o.value === status) || statusSelectOptions[0]}
          onChange={(option) => changeStatus(option?.value || "all")}
          placeholder={t("exam_taking.all_statuses")}
          noOptionsMessage={() => t("table.no_options")}
          isSearchable={false}
          onKeyDown={(event) => {
            // The first Escape closes the menu; with the menu closed (the input's aria-expanded) it closes the dialog.
            if (event.key === "Escape" && event.target.getAttribute?.("aria-expanded") !== "true") onClose();
          }}
        />
      ),
    },
  ];

  let content;
  if (failed) {
    content = (
      <div className={messageCard}>
        <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
          <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <p role="alert" className="text-sm font-medium text-slate-900">
          {t("evaluators.modal.load_failed")}
        </p>
        <button type="button" onClick={() => setAttempt((n) => n + 1)} className={secondaryButtonClass}>
          {t("evaluators.list.retry")}
        </button>
      </div>
    );
  } else if (loading && evaluations.length === 0) {
    content = (
      <div className={messageCard}>
        <div className="text-center" role="status">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true"></div>
          <p className="text-sm text-slate-500">{t("evaluators.loading_questions")}</p>
        </div>
      </div>
    );
  } else if (evaluations.length === 0) {
    content = (
      <div className={messageCard}>
        <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
          {listFiltered ? (
            <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
          ) : (
            <ClipboardDocumentCheckIcon className="h-6 w-6" aria-hidden="true" />
          )}
        </div>
        <h3 className="text-sm font-medium text-slate-900">{t("evaluators.no_evaluations")}</h3>
        {/* "Clear all" is the filters card's, right above: not repeated here. */}
        {listFiltered && <p className="mt-1 max-w-md text-sm text-slate-500">{t("evaluators.no_evaluations_hint")}</p>}
      </div>
    );
  } else {
    content = (
      <ul
        aria-busy={loading || undefined}
        className={`transition-opacity duration-200 ${loading ? "pointer-events-none opacity-60" : ""} ${
          wide
            ? "divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
            : "flex flex-col gap-2.5"
        }`}
      >
        {evaluations.map((evaluation) => (
          <EvaluationItem key={evaluation.uuid} evaluation={evaluation} wide={wide} language={language} />
        ))}
      </ul>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <FilterChips
        label={t("evaluators.my_evaluations.vote_filter")}
        allLabel={t("question_list.status_all")}
        items={voteItems}
        selected={vote}
        onSelect={changeVote}
      />

      <FilterPanel
        search={searchInput}
        onSearchChange={setSearchInput}
        searchLabel={t("evaluators.my_evaluations.search_label")}
        searchPlaceholder={t(
          wide ? "evaluators.my_evaluations.search_placeholder" : "evaluators.my_evaluations.search_placeholder_short"
        )}
        filters={filters}
        canClear={filtersActive}
        onClearAll={clearFilters}
      />

      {!failed && (
        <div ref={resultsRef} tabIndex={-1} className="scroll-mt-4 focus:outline-none">
          <ResultsBar
            countText={count === null ? " " : t("evaluators.my_evaluations.count", { count })}
            page={page}
            pageCount={pageCount}
            onPage={(next) => goToPage(next)}
            loading={loading}
          />
        </div>
      )}

      {content}

      {!failed && evaluations.length > 0 && pageCount > 1 && (
        <div className="flex justify-center">
          <Pager
            page={page}
            pageCount={pageCount}
            onPage={(next) => goToPage(next, { scroll: true })}
            disabled={loading}
            label={t("question_list.pagination_bottom")}
          />
        </div>
      )}
    </div>
  );
}

/**
 * An expert's evaluations, opened from the experts list: the expert (avatar, name, main subject) and their
 * evaluations with a vote filter, a search and a status filter, PAGE_SIZE at a time. `evaluator` is the list's
 * row ({uuid, full_name, domain_subject}); `onClose` runs on the close button, Escape and a click outside.
 */
export default function Modal({ isOpen, onClose, evaluator }) {
  const [t] = useTranslation();
  const closeRef = useRef(null);
  // The list forgets the expert as it closes the dialog: the closing dialog keeps showing the last one.
  const [shown, setShown] = useState(evaluator || null);
  if (evaluator && evaluator !== shown) setShown(evaluator);

  // The status filter's statuses, loaded at the first opening (and at the next one if that failed).
  const [statuses, setStatuses] = useState(null);
  useEffect(() => {
    if (!isOpen || statuses) return undefined;
    const controller = new AbortController();
    getStatuses({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((data) => setStatuses(Array.isArray(data) ? data : data?.results || []))
      .catch((err) => {
        if (!isCanceledError(err)) console.error("Failed to fetch statuses:", err);
      });
    return () => controller.abort();
  }, [isOpen, statuses]);

  const name = (shown?.full_name || "").trim();
  const subject = (typeof shown?.domain_subject === "string" ? shown.domain_subject : shown?.domain_subject?.name || "").trim();

  return (
    <Transition appear show={Boolean(isOpen && shown)} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={closeRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/50" aria-hidden="true" />
        </Transition.Child>

        {/* Phones: the whole screen (the filters get the page's width). Wider screens: a card as tall as its
            content, held at the top so the header stays put when a filter leaves fewer evaluations. */}
        <div className="fixed inset-0 flex items-center justify-center sm:items-start sm:p-6 lg:p-10">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="flex h-full max-h-full w-full max-w-5xl flex-col overflow-hidden bg-slate-50 shadow-2xl sm:h-auto sm:rounded-2xl sm:ring-1 sm:ring-slate-900/5">
              <div className="flex items-center gap-3 border-b border-slate-200 bg-white py-3 pl-4 pr-2 sm:py-4 sm:pl-6 sm:pr-4">
                <span
                  aria-hidden="true"
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ring-1 ring-inset ${AVATAR_TONE}`}
                >
                  {name ? initials(name) : <UserIcon className="h-5 w-5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <Dialog.Title
                    className={`break-words text-lg font-semibold leading-snug tracking-tight ${name ? "text-slate-900" : "italic text-slate-500"}`}
                  >
                    {name || t("evaluators.no_name")}
                  </Dialog.Title>
                  {subject && (
                    <p className="relative mt-0.5 break-words text-sm text-slate-500">
                      <span className="sr-only">{t("evaluators.table.main_subject")}: </span>
                      {subject}
                    </p>
                  )}
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={onClose}
                  aria-label={t("buttons.close")}
                  className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 sm:h-10 sm:w-10 ${focusRing}`}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {/* relative: react-select measures its menu against this scroll box, and sr-only texts stay in it. */}
              <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5">
                {shown && <EvaluatorEvaluations key={shown.uuid} evaluator={shown} statuses={statuses} onClose={onClose} />}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
